// Implements: F-UIX-018, F-USG-005
/**
 * The consumption of one organization or team over the report's range (UIX.5, RPT.4), as one row per
 * day, team or person in each currency (money is never added across currencies), each with the part
 * of its currency's total it is. The totals are the screen's tiles (`ConsumptionTiles`). Pure
 * presentation; money is formatted here, in the backoffice only (OD-12). Read-only: nothing here
 * writes.
 *
 * Rows are keyed by their subject and currency (checklist 14). A person row reads the server's own
 * words ("Persona eliminada", "Sin atribuir"); a team the organization no longer lists reads
 * "Equipo no disponible", never its id. The share is a native `<progress>` (a bar with a value, no
 * inline style) next to its percentage.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import { moneyText } from "../../../shared/ui/moneyText";
import type { ConsumptionGrouping, OrganizationConsumptionView } from "../../../types/reports";
import { rowSubject } from "../model/scopeReports";
import type { RowSubject } from "../model/scopeReports";
import styles from "./MyUsage.module.css";
import shareStyles from "./ScopeConsumption.module.css";

export interface ScopeConsumptionProps {
  readonly consumption: OrganizationConsumptionView;
  /** The grouping the rows were asked with. */
  readonly grouping: ConsumptionGrouping;
  /** Team names by id, for team rows; empty when they are not known. */
  readonly teamNames: ReadonlyMap<string, string>;
  /**
   * Mockup v3 P9 (owner decision): the groupings the card head offers as "Agrupar por"; the
   * toggle is drawn only when there is a choice.
   */
  readonly groupings?: readonly ConsumptionGrouping[] | undefined;
  readonly onGrouping?: ((grouping: ConsumptionGrouping) => void) | undefined;
}

/** The whole-number percentage a row is of its currency's total; 0 when the total is 0. */
export function shareOf(costMicros: number, totalMicros: number): number {
  return totalMicros <= 0 ? 0 : Math.round((costMicros / totalMicros) * 100);
}

export function ScopeConsumption({ consumption, grouping, teamNames, groupings = [], onGrouping }: ScopeConsumptionProps): ReactElement {
  const { t } = useTranslation();
  const rowsId = useId();
  const totals = new Map(consumption.totals.map((total) => [total.currency, total.cost_micros] as const));

  const subjectText = (subject: RowSubject): string => {
    switch (subject.kind) {
      case "text":
        return subject.text;
      case "noTeam":
        return t("backoffice.consumption.subject.noTeam");
      case "unknownTeam":
        return t("backoffice.consumption.subject.unknownTeam");
      case "noPerson":
        return t("backoffice.consumption.subject.noPerson");
    }
  };
  const groupingLabels: Readonly<Record<ConsumptionGrouping, string>> = {
    day: t("backoffice.consumption.grouping.day"),
    team: t("backoffice.consumption.grouping.team"),
    person: t("backoffice.consumption.grouping.person"),
  };
  const groupingLabel = groupingLabels[grouping];

  return (
    <section className={styles.section} aria-labelledby={rowsId}>
      <div className={shareStyles.head}>
        <h3 id={rowsId} className={styles.sectionTitle}>
          {grouping === "team"
            ? t("reports.consumption.rows.team")
            : grouping === "person"
              ? t("reports.consumption.rows.person")
              : t("backoffice.consumption.rows.title")}
        </h3>
        {onGrouping !== undefined && groupings.length > 1 ? (
          <div className={shareStyles.grouping}>
            {/* The visible words of v3's head; the group carries the same name for assistive tech. */}
            <span className={shareStyles.percent} aria-hidden="true">
              {t("backoffice.consumption.grouping.label")}
            </span>
            <SegmentedControl
              label={t("backoffice.consumption.grouping.label")}
              options={groupings.map((option) => ({ value: option, label: groupingLabels[option] }))}
              value={grouping}
              onChange={onGrouping}
            />
          </div>
        ) : null}
      </div>
      <table className={styles.table} aria-labelledby={rowsId}>
        <thead>
          <tr>
            <th scope="col">{groupingLabel}</th>
            <th scope="col">{t("backoffice.myUsage.column.currency")}</th>
            <th scope="col">{t("backoffice.myUsage.column.delivered")}</th>
            <th scope="col">{t("backoffice.myUsage.column.failed")}</th>
            <th scope="col">{t("backoffice.myUsage.column.cancelled")}</th>
            <th scope="col">{t("backoffice.myUsage.column.cost")}</th>
            <th scope="col">{t("backoffice.consumption.share")}</th>
          </tr>
        </thead>
        <tbody>
          {consumption.rows.map((row, index) => {
            const share = shareOf(row.cost_micros, totals.get(row.currency) ?? 0);
            return (
              // A deleted or unattributed person has no id, so the position breaks the tie.
              <tr key={`${row.day ?? ""}:${row.team_id ?? ""}:${row.person?.id ?? `-${String(index)}`}:${row.currency}`}>
                <th scope="row">{subjectText(rowSubject(row, grouping, teamNames))}</th>
                <td>{row.currency}</td>
                <td>{row.delivered}</td>
                <td>{row.failed}</td>
                <td>{row.cancelled}</td>
                <td>{moneyText(row.cost_micros, row.currency)}</td>
                <td>
                  <div className={shareStyles.share}>
                    <progress className={shareStyles.bar} value={share} max={100} aria-label={t("backoffice.consumption.share")} />
                    <span className={shareStyles.percent}>{`${String(share)} %`}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
